import React from 'react';
import { FiUsers, FiHeart, FiHome, FiDollarSign, FiCalendar, FiTrendingUp } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  CheckboxGroup,
  CheckboxLabel,
  InfoBox,
  TextArea
} from './FormStyles';
import { CoupleInfo } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from './DocumentUpload';
import { COUPLE_DOCUMENTS } from '@/config/documentsBySection';

interface CoupleSectionProps {
  data: Partial<CoupleInfo>;
  onChange: (data: Partial<CoupleInfo>) => void;
  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
  readonly?: boolean;
}

export const CoupleSection: React.FC<CoupleSectionProps> = ({
  data,
  onChange,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleUnionTypeChange = (type: string) => {
    handleChange('type_union', [type as any]);
  };

  const handleAideDifficulteChange = (type: string, checked: boolean) => {
    const currentTypes = data.aide_difficulte || [];
    const newTypes = checked
      ? [...currentTypes, type as any]
      : currentTypes.filter(t => t !== type);
    handleChange('aide_difficulte', newTypes);
  };

  const handleProjetsChange = (type: string, checked: boolean) => {
    const currentTypes = data.projets_communs || [];
    const newTypes = checked
      ? [...currentTypes, type as any]
      : currentTypes.filter(t => t !== type);
    handleChange('projets_communs', newTypes);
  };

  return (
    <FormSection>
      <SectionTitle>
        <FiUsers />
        Candidature en Couple
      </SectionTitle>

      <InfoBox $type="info">
        <FiHeart />
        <p>
          Si vous postulez en couple, nous aurons besoin des informations complètes sur votre conjoint(e)
          et votre organisation en tant que couple. Cela nous permettra de mieux évaluer la situation du foyer.
        </p>
      </InfoBox>

      <FormGroup>
        <Label>Type de candidature</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.candidature_couple || false}
            onChange={e => handleChange('candidature_couple', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.candidature_couple ? 'Je postule avec mon époux/épouse' : 'Je postule seul(e)'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.candidature_couple && (
        <>
          {/* ========== SECTION A : INFORMATIONS DU COUPLE ========== */}
          <SubSectionTitle>
            <FiHeart style={{ marginRight: '8px' }} />
            Section A : Informations du Couple
          </SubSectionTitle>

          <FormGroup>
            <Label $required>Type d'union</Label>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="type_union"
                  checked={(data.type_union || []).includes('Mariage civil')}
                  onChange={e => e.target.checked && handleUnionTypeChange('Mariage civil')}
                  disabled={readonly}
                />
                Mariage civil
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="type_union"
                  checked={(data.type_union || []).includes('Mariage coutumier')}
                  onChange={e => e.target.checked && handleUnionTypeChange('Mariage coutumier')}
                  disabled={readonly}
                />
                Mariage coutumier
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="type_union"
                  checked={(data.type_union || []).includes('Mariage religieux')}
                  onChange={e => e.target.checked && handleUnionTypeChange('Mariage religieux')}
                  disabled={readonly}
                />
                Mariage religieux
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="type_union"
                  checked={(data.type_union || []).includes('Union libre')}
                  onChange={e => e.target.checked && handleUnionTypeChange('Union libre')}
                  disabled={readonly}
                />
                Union libre
              </CheckboxLabel>
            </CheckboxGroup>
          </FormGroup>

          {/* Mariage civil */}
          {(data.type_union || []).includes('Mariage civil') && (
            <>
              <FormGrid>
                <FormGroup>
                  <Label>Date du mariage civil</Label>
                  <Input
                    type="date"
                    value={data.date_mariage || ''}
                    onChange={e => handleChange('date_mariage', e.target.value)}
                    disabled={readonly}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Lieu du mariage</Label>
                  <Input
                    type="text"
                    value={data.lieu_mariage || ''}
                    onChange={e => handleChange('lieu_mariage', e.target.value)}
                    placeholder="Ville, Commune..."
                    disabled={readonly}
                  />
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <Label>Régime matrimonial</Label>
                <Select
                  value={data.regime_matrimonial || ''}
                  onChange={e => handleChange('regime_matrimonial', e.target.value)}
                  disabled={readonly}
                >
                  <option value="">Sélectionnez...</option>
                  <option value="Communauté de biens">Communauté de biens</option>
                  <option value="Séparation de biens">Séparation de biens</option>
                  <option value="Régime coutumier">Régime coutumier</option>
                  <option value="Ne sait pas">Ne sait pas</option>
                </Select>
              </FormGroup>
            </>
          )}

          {/* Mariage coutumier */}
          {(data.type_union || []).includes('Mariage coutumier') && (
            <>
              <FormGrid>
                <FormGroup>
                  <Label>Date du mariage coutumier</Label>
                  <Input
                    type="date"
                    value={data.date_mariage || ''}
                    onChange={e => handleChange('date_mariage', e.target.value)}
                    disabled={readonly}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Témoins disponibles ?</Label>
                  <Select
                    value={data.temoins_disponibles === undefined ? '' : data.temoins_disponibles ? 'Oui' : 'Non'}
                    onChange={e => handleChange('temoins_disponibles', e.target.value === 'Oui')}
                    disabled={readonly}
                  >
                    <option value="">Sélectionnez...</option>
                    <option value="Oui">Oui</option>
                    <option value="Non">Non</option>
                  </Select>
                </FormGroup>
              </FormGrid>
            </>
          )}

          {/* Mariage religieux */}
          {(data.type_union || []).includes('Mariage religieux') && (
            <>
              <FormGrid>
                <FormGroup>
                  <Label>Date du mariage religieux</Label>
                  <Input
                    type="date"
                    value={data.date_mariage || ''}
                    onChange={e => handleChange('date_mariage', e.target.value)}
                    disabled={readonly}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Lieu de culte</Label>
                  <Input
                    type="text"
                    value={data.lieu_culte || ''}
                    onChange={e => handleChange('lieu_culte', e.target.value)}
                    placeholder="Nom de l'église, mosquée..."
                    disabled={readonly}
                  />
                </FormGroup>
              </FormGrid>
            </>
          )}

          {/* Union libre */}
          {(data.type_union || []).includes('Union libre') && (
            <>
              <FormGrid>
                <FormGroup>
                  <Label>Depuis combien d'années ?</Label>
                  <Input
                    type="number"
                    value={data.depuis_annees || ''}
                    onChange={e => handleChange('depuis_annees', parseInt(e.target.value))}
                    placeholder="Années"
                    disabled={readonly}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Adresse commune depuis (années)</Label>
                  <Input
                    type="number"
                    value={data.adresse_commune_depuis_annees || ''}
                    onChange={e => handleChange('adresse_commune_depuis_annees', parseInt(e.target.value))}
                    placeholder="Années"
                    disabled={readonly}
                  />
                </FormGroup>
              </FormGrid>
            </>
          )}

          <FormGrid>
            <FormGroup>
              <Label>Enfants communs</Label>
              <Input
                type="number"
                value={data.enfants_communs || ''}
                onChange={e => handleChange('enfants_communs', parseInt(e.target.value))}
                placeholder="Nombre"
                disabled={readonly}
              />
            </FormGroup>
            <FormGroup>
              <Label>Enfants d'unions précédentes</Label>
              <Input
                type="number"
                value={data.enfants_unions_precedentes || ''}
                onChange={e => handleChange('enfants_unions_precedentes', parseInt(e.target.value))}
                placeholder="Nombre"
                disabled={readonly}
              />
            </FormGroup>
          </FormGrid>

          {/* ========== SECTION B : CANDIDAT PRINCIPAL ========== */}
          <SubSectionTitle>
            <FiUsers style={{ marginRight: '8px' }} />
            Section B : Candidat Principal
          </SubSectionTitle>

          <FormGroup>
            <Label $required>Qui sera le titulaire principal du bail ?</Label>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="titulaire_principal"
                  checked={data.titulaire_principal === 'Homme'}
                  onChange={e => e.target.checked && handleChange('titulaire_principal', 'Homme')}
                  disabled={readonly}
                />
                L'homme
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="titulaire_principal"
                  checked={data.titulaire_principal === 'Femme'}
                  onChange={e => e.target.checked && handleChange('titulaire_principal', 'Femme')}
                  disabled={readonly}
                />
                La femme
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="titulaire_principal"
                  checked={data.titulaire_principal === 'Co-titulaires'}
                  onChange={e => e.target.checked && handleChange('titulaire_principal', 'Co-titulaires')}
                  disabled={readonly}
                />
                Les deux (co-titulaires)
              </CheckboxLabel>
            </CheckboxGroup>
          </FormGroup>

          <FormGrid>
            <FormGroup>
              <Label $required>Catégorie du candidat</Label>
              <Select
                value={data.categorie_candidat || ''}
                onChange={e => handleChange('categorie_candidat', e.target.value)}
                disabled={readonly}
              >
                <option value="">Sélectionnez...</option>
                <option value="Salarié">Salarié</option>
                <option value="Entrepreneur">Entrepreneur</option>
                <option value="Informel">Informel</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Revenus mensuels (FCFA)</Label>
              <Input
                type="number"
                value={data.revenus_candidat || ''}
                onChange={e => handleChange('revenus_candidat', parseInt(e.target.value))}
                placeholder="Montant"
                disabled={readonly}
              />
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <Label>Contribution au loyer (%)</Label>
            <Input
              type="number"
              value={data.contribution_loyer_candidat || ''}
              onChange={e => handleChange('contribution_loyer_candidat', parseInt(e.target.value))}
              placeholder="Ex: 60"
              min="0"
              max="100"
              disabled={readonly}
            />
            <small style={{ color: '#666', fontSize: '13px' }}>Exemple : 60% si vous payez 60% du loyer</small>
          </FormGroup>

          {/* ========== SECTION C : CONJOINT ========== */}
          <SubSectionTitle>
            <FiHeart style={{ marginRight: '8px' }} />
            Section C : Conjoint
          </SubSectionTitle>

          <FormGrid>
            <FormGroup>
              <Label $required>Nom du conjoint</Label>
              <Input
                type="text"
                value={data.nom_conjoint || ''}
                onChange={e => handleChange('nom_conjoint', e.target.value)}
                placeholder="Nom"
                disabled={readonly}
              />
            </FormGroup>
            <FormGroup>
              <Label $required>Prénom(s) du conjoint</Label>
              <Input
                type="text"
                value={data.prenoms_conjoint || ''}
                onChange={e => handleChange('prenoms_conjoint', e.target.value)}
                placeholder="Prénom(s)"
                disabled={readonly}
              />
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <Label>Date de naissance du conjoint</Label>
            <Input
              type="date"
              value={data.date_naissance_conjoint || ''}
              onChange={e => handleChange('date_naissance_conjoint', e.target.value)}
              disabled={readonly}
            />
          </FormGroup>

          <FormGrid>
            <FormGroup>
              <Label $required>Catégorie du conjoint</Label>
              <Select
                value={data.categorie_conjoint || ''}
                onChange={e => handleChange('categorie_conjoint', e.target.value)}
                disabled={readonly}
              >
                <option value="">Sélectionnez...</option>
                <option value="Salarié">Salarié</option>
                <option value="Entrepreneur">Entrepreneur</option>
                <option value="Informel">Informel</option>
                <option value="Sans activité">Sans activité</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Revenus mensuels (FCFA)</Label>
              <Input
                type="number"
                value={data.revenus_conjoint || ''}
                onChange={e => handleChange('revenus_conjoint', parseInt(e.target.value))}
                placeholder="Montant"
                disabled={readonly || data.categorie_conjoint === 'Sans activité'}
              />
            </FormGroup>
          </FormGrid>

          {data.categorie_conjoint !== 'Sans activité' && (
            <FormGroup>
              <Label>Contribution au loyer (%)</Label>
              <Input
                type="number"
                value={data.contribution_loyer_conjoint || ''}
                onChange={e => handleChange('contribution_loyer_conjoint', parseInt(e.target.value))}
                placeholder="Ex: 40"
                min="0"
                max="100"
                disabled={readonly}
              />
              <small style={{ color: '#666', fontSize: '13px' }}>Exemple : 40% si votre conjoint paie 40% du loyer</small>
            </FormGroup>
          )}

          <FormGroup>
            <Label>Le conjoint travaille-t-il avec vous ?</Label>
            <Select
              value={data.conjoint_travaille_avec || ''}
              onChange={e => handleChange('conjoint_travaille_avec', e.target.value)}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>
              <option value="Non">Non, activités séparées</option>
              <option value="Même entreprise">Oui, même entreprise</option>
              <option value="Collaboration">Oui, collaboration</option>
            </Select>
          </FormGroup>

          {data.conjoint_travaille_avec === 'Même entreprise' && (
            <FormGroup>
              <Label>Rôle du conjoint dans l'entreprise</Label>
              <Input
                type="text"
                value={data.role_conjoint_entreprise || ''}
                onChange={e => handleChange('role_conjoint_entreprise', e.target.value)}
                placeholder="Ex: Comptable, Responsable commercial..."
                disabled={readonly}
              />
            </FormGroup>
          )}

          {data.conjoint_travaille_avec === 'Collaboration' && (
            <FormGroup>
              <Label>Détails de la collaboration</Label>
              <TextArea
                value={data.details_collaboration || ''}
                onChange={e => handleChange('details_collaboration', e.target.value)}
                placeholder="Décrivez comment vous collaborez..."
                disabled={readonly}
                rows={3}
              />
            </FormGroup>
          )}

          {/* ========== SECTION D : ORGANISATION FINANCIÈRE DU COUPLE ========== */}
          <SubSectionTitle>
            <FiDollarSign style={{ marginRight: '8px' }} />
            Section D : Organisation Financière du Couple
          </SubSectionTitle>

          <FormGroup>
            <Label $required>Comment gérez-vous l'argent du foyer ?</Label>
            <Select
              value={data.gestion_argent || ''}
              onChange={e => handleChange('gestion_argent', e.target.value)}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>
              <option value="Compte commun">Compte commun</option>
              <option value="Comptes séparés">Comptes séparés</option>
              <option value="Gestion par un seul">Gestion par un seul</option>
              <option value="Caisse commune informelle">Caisse commune informelle</option>
              <option value="Autre">Autre</option>
            </Select>
          </FormGroup>

          {data.gestion_argent === 'Autre' && (
            <FormGroup>
              <Label>Précisez</Label>
              <Input
                type="text"
                value={data.gestion_argent_autre || ''}
                onChange={e => handleChange('gestion_argent_autre', e.target.value)}
                placeholder="Décrivez votre mode de gestion..."
                disabled={readonly}
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label $required>Qui paiera le loyer ?</Label>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="qui_paiera_loyer"
                  checked={data.qui_paiera_loyer === 'Candidat principal seul'}
                  onChange={e => e.target.checked && handleChange('qui_paiera_loyer', 'Candidat principal seul')}
                  disabled={readonly}
                />
                Le candidat principal seul
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="qui_paiera_loyer"
                  checked={data.qui_paiera_loyer === 'Conjoint seul'}
                  onChange={e => e.target.checked && handleChange('qui_paiera_loyer', 'Conjoint seul')}
                  disabled={readonly}
                />
                Le conjoint seul
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="qui_paiera_loyer"
                  checked={data.qui_paiera_loyer === 'Les deux ensemble'}
                  onChange={e => e.target.checked && handleChange('qui_paiera_loyer', 'Les deux ensemble')}
                  disabled={readonly}
                />
                Les deux ensemble
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="qui_paiera_loyer"
                  checked={data.qui_paiera_loyer === 'Alternance'}
                  onChange={e => e.target.checked && handleChange('qui_paiera_loyer', 'Alternance')}
                  disabled={readonly}
                />
                Alternance
              </CheckboxLabel>
            </CheckboxGroup>
          </FormGroup>

          <FormGroup>
            <Label>En cas de difficulté, qui aidera ?</Label>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.aide_difficulte || []).includes('Famille du candidat')}
                  onChange={e => handleAideDifficulteChange('Famille du candidat', e.target.checked)}
                  disabled={readonly}
                />
                Famille du candidat
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.aide_difficulte || []).includes('Famille du conjoint')}
                  onChange={e => handleAideDifficulteChange('Famille du conjoint', e.target.checked)}
                  disabled={readonly}
                />
                Famille du conjoint
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.aide_difficulte || []).includes('Les deux familles')}
                  onChange={e => handleAideDifficulteChange('Les deux familles', e.target.checked)}
                  disabled={readonly}
                />
                Les deux familles
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.aide_difficulte || []).includes('Tontine commune')}
                  onChange={e => handleAideDifficulteChange('Tontine commune', e.target.checked)}
                  disabled={readonly}
                />
                Tontine commune
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.aide_difficulte || []).includes('Autre')}
                  onChange={e => handleAideDifficulteChange('Autre', e.target.checked)}
                  disabled={readonly}
                />
                Autre
              </CheckboxLabel>
            </CheckboxGroup>
          </FormGroup>

          {(data.aide_difficulte || []).includes('Autre') && (
            <FormGroup>
              <Label>Précisez</Label>
              <Input
                type="text"
                value={data.aide_difficulte_autre || ''}
                onChange={e => handleChange('aide_difficulte_autre', e.target.value)}
                placeholder="Autre source d'aide..."
                disabled={readonly}
              />
            </FormGroup>
          )}

          {/* ========== SECTION E : HISTORIQUE COMMUN ========== */}
          <SubSectionTitle>
            <FiCalendar style={{ marginRight: '8px' }} />
            Section E : Historique Commun
          </SubSectionTitle>

          <FormGroup>
            <Label>Avez-vous déjà loué ensemble ?</Label>
            <Select
              value={data.deja_loue_ensemble === undefined ? '' : data.deja_loue_ensemble ? 'Oui' : 'Non'}
              onChange={e => handleChange('deja_loue_ensemble', e.target.value === 'Oui')}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>
              <option value="Oui">Oui</option>
              <option value="Non">Non, première fois</option>
            </Select>
          </FormGroup>

          {data.deja_loue_ensemble && (
            <>
              <FormGroup>
                <Label>Durée de location ensemble (années)</Label>
                <Input
                  type="number"
                  value={data.duree_location_ensemble || ''}
                  onChange={e => handleChange('duree_location_ensemble', parseInt(e.target.value))}
                  placeholder="Nombre d'années"
                  disabled={readonly}
                />
              </FormGroup>

              <FormGrid>
                <FormGroup>
                  <Label>Propriétaire précédent</Label>
                  <Input
                    type="text"
                    value={data.proprietaire_precedent || ''}
                    onChange={e => handleChange('proprietaire_precedent', e.target.value)}
                    placeholder="Nom du propriétaire"
                    disabled={readonly}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Téléphone du propriétaire</Label>
                  <Input
                    type="tel"
                    value={data.telephone_proprietaire_precedent || ''}
                    onChange={e => handleChange('telephone_proprietaire_precedent', e.target.value)}
                    placeholder="Téléphone"
                    disabled={readonly}
                  />
                </FormGroup>
              </FormGrid>
            </>
          )}

          <FormGroup>
            <Label>Projets communs</Label>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.projets_communs || []).includes('Avoir des enfants')}
                  onChange={e => handleProjetsChange('Avoir des enfants', e.target.checked)}
                  disabled={readonly}
                />
                Avoir des enfants
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.projets_communs || []).includes('Développer un business ensemble')}
                  onChange={e => handleProjetsChange('Développer un business ensemble', e.target.checked)}
                  disabled={readonly}
                />
                Développer un business ensemble
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.projets_communs || []).includes('Construire une maison')}
                  onChange={e => handleProjetsChange('Construire une maison', e.target.checked)}
                  disabled={readonly}
                />
                Construire une maison
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={(data.projets_communs || []).includes('Autre')}
                  onChange={e => handleProjetsChange('Autre', e.target.checked)}
                  disabled={readonly}
                />
                Autre
              </CheckboxLabel>
            </CheckboxGroup>
          </FormGroup>

          {(data.projets_communs || []).includes('Avoir des enfants') && (
            <FormGroup>
              <Label>Combien d'enfants souhaitez-vous avoir ?</Label>
              <Input
                type="number"
                value={data.nombre_enfants_souhaite || ''}
                onChange={e => handleChange('nombre_enfants_souhaite', parseInt(e.target.value))}
                placeholder="Nombre"
                disabled={readonly}
              />
            </FormGroup>
          )}

          {(data.projets_communs || []).includes('Autre') && (
            <FormGroup>
              <Label>Précisez votre autre projet</Label>
              <TextArea
                value={data.autre_projet || ''}
                onChange={e => handleChange('autre_projet', e.target.value)}
                placeholder="Décrivez votre projet..."
                disabled={readonly}
                rows={3}
              />
            </FormGroup>
          )}

          {/* Documents justificatifs pour cette section */}
          <InfoBox $type="warning" style={{ marginTop: '24px' }}>
            <FiHome />
            <div>
              <strong>Documents spécifiques couples requis :</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Preuve d'union (acte de mariage, certificat, témoignages, etc.)</li>
                <li>Photos du couple (3-5 photos à différentes périodes)</li>
                <li>Déclaration d'engagement solidaire</li>
                <li>Livret de famille (si enfants) - Recommandé</li>
                <li>Factures communes (électricité, eau) - Recommandé</li>
                <li>Attestation de vie commune du chef de quartier - Recommandé</li>
              </ul>
            </div>
          </InfoBox>

          {onDocumentUpload && onDocumentRemove && (
            <DocumentUpload
              sectionTitle="Candidature en Couple"
              documents={COUPLE_DOCUMENTS}
              uploadedFiles={uploadedDocuments}
              onFileSelect={onDocumentUpload}
              onFileRemove={onDocumentRemove}
              readonly={readonly}
            />
          )}
        </>
      )}
    </FormSection>
  );
};
